import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from '../../components/Icon.jsx';
import { placePhotoUrl } from '../../lib/api.js';
import { useSwipeGesture } from '../discover/useSwipeGesture.js';
import { color, font, radius } from '../../theme/tokens.js';
import { venues } from './venues.generated.js';

/** The warm off-white the whole page is written in. */
const CREAM = '#F7F4ED';

/**
 * The four seats, clockwise from the top.
 *
 * `You` sits at the bottom, nearest the thumb — it is the only one a visitor
 * can actually swipe, and reaching over the other three to do it would be a
 * strange way to arrange a demo.
 */
const SEATS = [
  { id: 'K', label: 'K', angle: -90 },
  { id: 'M', label: 'M', angle: 0 },
  { id: 'you', label: 'You', angle: 90, mine: true },
  { id: 'J', label: 'J', angle: 180 },
];

/**
 * What the friends do, round by round.
 *
 * The first round is not a match: two of them swipe left, so the table moves
 * on. That is the honest shape of the thing — a group does not agree on the
 * first place it sees — and it makes the round that *does* land mean something.
 * The visitor's seat is never scripted; `passes` only ever names friends.
 */
const ROUNDS = [{ passes: ['M', 'J'] }, { passes: [] }];

/** When each friend's vote lands, in seat order. Staggered so they arrive one at a time. */
const FRIEND_VOTE_MS = [700, 1250, 1800];

/** How long the visitor is left to cast their own before the reel does it for them. */
const YOUR_TURN_MS = 2600;

/** How long a decision holds before the next round, and how briefly a miss does. */
const HOLD_MS = 2600;
const MISS_MS = 900;

/**
 * Ring geometry, as percentages of the ring's width. It is square, so these all
 * read the same way across and down, and the whole arrangement scales with it.
 *
 * They have to stay in proportion: a seat's furthest corner is ORBIT + CARD_H/2
 * from the middle and must stay under 50, while ORBIT - CARD_H/2 must stay
 * above TALLY/2 or the cards will sit on top of the count.
 */
const ORBIT = 33;
const CARD_W = 20;
const CARD_H = 25;
const TALLY = 30;

/**
 * The ring width every fixed pixel size in this file was drawn against.
 *
 * The seats and the tally are placed in percentages, so the *arrangement*
 * already scales with the ring. Their contents did not. The count, the labels
 * and the icons were fixed pixels, so on a short screen — where the ring only
 * gets the height left over after the wordmark, the label, the closing line and
 * the form — the tally kept 28px digits and a 26px tick inside a circle that
 * had shrunk to a third of the size they were drawn for. `said yes`, `Agreed`
 * and `Not this one` ran out of it, and because `stage` is `overflow: hidden`
 * they were cut off rather than visibly spilling.
 */
const RING_BASIS = 380;

/**
 * Four people deciding at once.
 *
 * The individual page shows one person swiping. This shows what the app is
 * actually for: everybody swiping on their own phone at the same time, and the
 * agreement arriving in the middle. A single card cannot say that — a ring of
 * four, all moving, says it before anyone reads a word.
 *
 * Three of the seats vote on a timer. The fourth is the visitor's, at the
 * bottom, and it is draggable: casting the deciding vote yourself is the whole
 * point of the feature, so the demo lets you do exactly that. If it is left
 * alone the reel votes for you and carries on, because a loop that stalls
 * waiting for a stranger is a loop that mostly sits still.
 */
export function GroupRing() {
  const [round, setRound] = useState(0);
  const [votes, setVotes] = useState(/** @type {Record<string, 'yes'|'no'>} */ ({}));
  const stillMotion = usePrefersReducedMotion();
  const [ringBoxRef, ringSide] = useRingSize();
  const scale = ringSide / RING_BASIS;

  const venue = venues[round % venues.length];
  const script = ROUNDS[round % ROUNDS.length];
  const cast = Object.keys(votes).length;
  const yes = Object.values(votes).filter((v) => v === 'yes').length;
  const settled = cast >= SEATS.length;
  const decided = settled && yes === SEATS.length;

  // The friends arrive on a stagger, then the visitor's seat is left open for a
  // beat before the reel fills it in.
  useEffect(() => {
    if (stillMotion || settled) return;

    const friends = SEATS.filter((seat) => !seat.mine);
    const timers = friends.map((seat, i) =>
      window.setTimeout(
        () =>
          setVotes((v) => ({ ...v, [seat.id]: script.passes.includes(seat.id) ? 'no' : 'yes' })),
        FRIEND_VOTE_MS[i]
      )
    );
    timers.push(
      window.setTimeout(() => setVotes((v) => ({ ...v, you: v.you ?? 'yes' })), YOUR_TURN_MS)
    );

    return () => timers.forEach(window.clearTimeout);
  }, [round, stillMotion, settled, script]);

  // Hold the result, then move on to the next place. A match is worth looking
  // at; a miss is not, so it clears quickly.
  useEffect(() => {
    if (!settled || stillMotion) return;
    const timer = window.setTimeout(
      () => {
        setRound((n) => n + 1);
        setVotes({});
      },
      decided ? HOLD_MS : MISS_MS
    );
    return () => window.clearTimeout(timer);
  }, [settled, decided, stillMotion]);

  return (
    <div style={styles.reel}>
      <p style={styles.srOnly}>
        Crave settles it for the whole table. Share a group code, everyone swipes on their own
        phone, and the first place you all say yes to wins.
      </p>

      <div style={styles.stage} aria-hidden="true">
        <p style={styles.nowVoting}>
          <span style={styles.nowVotingName}>{venue.name}</span>
          {venue.cuisine ? ` · ${venue.cuisine}` : ''}
          {venue.price ? ` · ${venue.price}` : ''}
        </p>

        <div ref={ringBoxRef} style={styles.ringBox}>
          <div style={{ ...styles.ring, width: ringSide, height: ringSide }}>
            {SEATS.map((seat) => (
              <Seat
                key={seat.id + round}
                seat={seat}
                venue={venue}
                scale={scale}
                vote={votes[seat.id] ?? null}
                onVote={(direction) =>
                  setVotes((v) => ({ ...v, [seat.id]: direction === 'right' ? 'yes' : 'no' }))
                }
              />
            ))}

            <Tally
              yes={yes}
              total={SEATS.length}
              settled={settled}
              decided={decided}
              scale={scale}
            />
          </div>
        </div>

        <p className="reel-closer" style={styles.closer}>
          {decided ? (
            <>
              <span style={styles.closerStrong}>That&rsquo;s dinner.</span> No group chat required.
            </>
          ) : (
            <>
              Everyone swipes at once.{' '}
              <span style={styles.closerStrong}>Yours is the one at the bottom.</span>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

/**
 * The middle of the ring: how many have said yes, then what they said yes to.
 *
 * @param {object} props
 * @param {number} props.yes
 * @param {number} props.total
 * @param {boolean} props.settled  Everyone has voted.
 * @param {boolean} props.decided  Everyone has voted yes.
 * @param {number} props.scale  How far the ring is from the size it was drawn at.
 */
function Tally({ yes, total, settled, decided, scale }) {
  const sized = scaledStyles(scale);

  if (decided) {
    return (
      <div style={{ ...styles.tally, ...sized.tally, ...styles.tallyDecided }}>
        <Icon name="check" size={26 * scale} stroke={color.primaryDark} strokeWidth={3} />
        <p style={{ ...styles.tallyName, ...sized.tallyName }}>Agreed</p>
      </div>
    );
  }

  return (
    <div style={{ ...styles.tally, ...sized.tally }}>
      {settled ? (
        <p style={{ ...styles.tallyMiss, ...sized.tallyMiss }}>Not this one</p>
      ) : (
        <>
          <p style={{ ...styles.tallyCount, ...sized.tallyCount }}>
            {yes}
            <span style={{ ...styles.tallyOf, ...sized.tallyOf }}>/{total}</span>
          </p>
          <p style={{ ...styles.tallyLabel, ...sized.tallyLabel }}>said yes</p>
        </>
      )}
    </div>
  );
}

/**
 * One person's seat: a small card over an initial, flicked away when they vote.
 *
 * The initial sits *behind* the card rather than beside it, so a seat takes up
 * no more room than its card and the ring stays clear of the tally. The card
 * leaving is what reveals who voted.
 *
 * The visitor's own seat is draggable. Everyone else's is not — a demo where
 * you can vote on your friends' behalf is showing the wrong product.
 *
 * @param {object} props
 * @param {(typeof SEATS)[number]} props.seat
 * @param {(typeof venues)[number]} props.venue
 * @param {'yes'|'no'|null} props.vote
 * @param {(direction: 'left'|'right') => void} props.onVote
 * @param {number} props.scale  How far the ring is from the size it was drawn at.
 */
function Seat({ seat, venue, vote, onVote, scale }) {
  const { offsetX, isDragging, handlers } = useSwipeGesture({
    enabled: Boolean(seat.mine),
    onCommit: onVote,
  });

  const sized = scaledStyles(scale);
  const radians = (seat.angle * Math.PI) / 180;
  // A friend's vote is a state change rather than a gesture, so their card is
  // thrown by the same prop that records it.
  const thrown = vote ? (vote === 'yes' ? 1 : -1) : 0;
  const stamp = vote ?? (offsetX > 24 ? 'yes' : offsetX < -24 ? 'no' : null);
  const stampStrength = vote ? 1 : Math.min(Math.abs(offsetX) / 60, 1);

  return (
    <div
      style={{
        ...styles.seat,
        left: `${50 + ORBIT * Math.cos(radians)}%`,
        top: `${50 + ORBIT * Math.sin(radians)}%`,
      }}
    >
      <div style={{ ...styles.avatar, ...sized.avatar }}>
        {vote === 'yes' ? (
          <Icon name="check" size={13 * scale} stroke={CREAM} strokeWidth={3} />
        ) : vote === 'no' ? (
          <Icon name="close" size={12 * scale} stroke="rgba(247,244,237,0.7)" strokeWidth={2.6} />
        ) : (
          seat.label
        )}
      </div>

      <div
        {...(seat.mine ? handlers : {})}
        style={{
          ...styles.card,
          ...sized.card,
          cursor: seat.mine ? (isDragging ? 'grabbing' : 'grab') : 'default',
          ...cardMotion({ thrown, offsetX, isDragging }),
        }}
      >
        {venue.photo && <img src={placePhotoUrl(venue.photo)} alt="" style={styles.photo} />}
        <div style={styles.cardScrim} />

        {stamp && (
          <div
            style={{
              ...styles.stamp,
              ...sized.stamp,
              opacity: stampStrength,
              background: stamp === 'yes' ? color.success : color.danger,
            }}
          >
            <Icon
              name={stamp === 'yes' ? 'check' : 'close'}
              size={12 * scale}
              stroke={CREAM}
              strokeWidth={3}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Where a seat's card sits while it is being dragged, and where it goes after. */
function cardMotion({ thrown, offsetX, isDragging }) {
  if (thrown) {
    return {
      // A percentage of the card's own width rather than a fixed distance: the
      // ring shrinks with the screen, and 74px that reads as a flick on a
      // 380px ring throws the card clean across a 220px one.
      transform: `translateX(${thrown * 130}%) rotate(${thrown * 18}deg)`,
      opacity: 0,
      transition: 'transform 0.34s ease-in, opacity 0.34s ease-in',
    };
  }
  return {
    transform: `translateX(${offsetX}px) rotate(${offsetX * 0.06}deg)`,
    opacity: 1,
    transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.2, 0.9, 0.3, 1.1)',
  };
}

/**
 * The side of the largest square that fits the room the ring has been given,
 * capped at the size it was drawn for.
 *
 * The ring used to size itself in CSS, and got it wrong: `height: 100%` with
 * `aspectRatio: 1` and `maxWidth`. A specified height is definite, so the
 * max-width clamped the width without the ratio pulling the height back down
 * with it — the "square" was 342×460 on an iPhone 15. Every percentage in this
 * file is read against both axes, so the seats sat on an ellipse, the cards
 * stretched tall, and the round tally became an oval with the same fixed-size
 * text still inside it.
 *
 * `min(width, height)` is the part CSS could not express here, and RING_BASIS
 * is the cap: past it the ring stops growing rather than blowing itself up on a
 * desktop window.
 *
 * Measured rather than worked out from the viewport, because the room in
 * question is whatever vertical space is left once the wordmark, the label, the
 * closing line and the waitlist form have taken theirs — no media query knows
 * that number.
 */
function useRingSize() {
  const ref = useRef(null);
  const [side, setSide] = useState(0);

  // Layout effect, and measured once by hand before the observer is attached:
  // a ResizeObserver does not report until after the first paint, so anything
  // this starts at is a frame the visitor actually sees. Starting at zero and
  // filling it in before the browser paints costs one invisible frame; starting
  // at RING_BASIS would flash a ring wider than the phone and snap it down.
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    // A hidden or not-yet-laid-out box measures 0. Ignore that reading rather
    // than collapsing the ring onto it.
    const measure = ({ width, height }) => {
      if (width > 0 && height > 0) setSide(Math.min(width, height, RING_BASIS));
    };

    measure(node.getBoundingClientRect());

    const observer = new ResizeObserver(([entry]) => measure(entry.contentRect));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, side];
}

/**
 * The pieces sized in pixels rather than percentages, resolved against the
 * ring's current scale.
 *
 * They live together here rather than inline so there is one list of what still
 * needs scaling — the failure mode this fixes is a value that quietly stayed
 * fixed while everything around it moved.
 *
 * @param {number} scale
 */
function scaledStyles(scale) {
  const px = (value) => value * scale;

  return {
    avatar: {
      width: px(40),
      height: px(40),
      marginTop: px(-20),
      marginLeft: px(-20),
      fontSize: px(12),
    },
    card: {
      borderRadius: px(15),
      boxShadow: `0 ${px(8)}px ${px(20)}px rgba(10,16,4,0.32)`,
    },
    stamp: { top: px(6), left: px(6), width: px(21), height: px(21) },

    tally: { gap: px(3), padding: `0 ${px(10)}px` },
    tallyCount: { fontSize: px(28) },
    tallyOf: { fontSize: px(16) },
    tallyLabel: { fontSize: px(10), letterSpacing: `${px(1)}px` },
    tallyMiss: { fontSize: px(12) },
    tallyName: { fontSize: px(13) },
  };
}

/** Whether the visitor has asked their system for reduced motion. */
function usePrefersReducedMotion() {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = (event) => setReduced(event.matches);
    media.addEventListener('change', onChange);
    setReduced(media.matches);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

const styles = {
  reel: { position: 'relative', zIndex: 0, width: '100%', height: '100%' },
  // Clipped, because a card thrown right from the right-hand seat travels past
  // the column edge and would otherwise widen the page into a horizontal
  // scrollbar. It is fading to nothing as it goes, so the edge does not read as
  // a cut.
  stage: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
  },

  // Takes whatever vertical room is left between the label and the closing
  // line, and gives the ring a definite height to size itself against.
  ringBox: {
    flex: 1,
    // A floor, so a short screen shrinks the ring only so far and then pushes
    // the page taller instead — `.landing-scroll` is there to reach the rest.
    // Without it `flex: 1` will happily hand the ring nothing at all.
    minHeight: 220,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Square, so the four seats sit on a real circle rather than an ellipse, and
  // so every percentage below means the same thing across and down.
  //
  // Both axes come from `useRingSize` as one measured number. Sizing it by
  // height alone made it 342px wide whatever the screen height, which is how it
  // came to sit on top of the phone-number field on a shorter phone; adding a
  // `maxWidth` to that then broke the ratio instead of holding it.
  ring: { position: 'relative' },

  // Placed by angle. Sizes are percentages of the ring so the whole arrangement
  // scales with it — nothing here is tuned to one viewport width.
  seat: {
    position: 'absolute',
    width: `${CARD_W}%`,
    height: `${CARD_H}%`,
    transform: 'translate(-50%, -50%)',
  },

  // Behind the card, revealed when it is thrown.
  avatar: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    borderRadius: radius.pill,
    border: '1.5px solid rgba(247,244,237,0.34)',
    background: 'rgba(255,255,255,0.10)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: font.weight.bold,
    color: 'rgba(247,244,237,0.78)',
  },

  card: {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    background: color.onDark,
    userSelect: 'none',
    touchAction: 'none',
  },
  photo: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' },
  cardScrim: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.10), rgba(0,0,0,0.55))',
  },
  stamp: {
    position: 'absolute',
    borderRadius: radius.pill,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.18s ease',
  },

  tally: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: `${TALLY}%`,
    height: `${TALLY}%`,
    transform: 'translate(-50%, -50%)',
    borderRadius: radius.pill,
    border: '1.5px solid rgba(247,244,237,0.30)',
    background: 'rgba(255,255,255,0.10)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    transition: 'background 0.35s ease, border-color 0.35s ease',
  },
  tallyDecided: { background: CREAM, borderColor: CREAM },

  tallyCount: {
    margin: 0,
    lineHeight: 1,
    fontWeight: font.weight.black,
    letterSpacing: '-1px',
    color: CREAM,
  },
  tallyOf: { color: 'rgba(247,244,237,0.55)' },
  tallyLabel: {
    margin: 0,
    fontWeight: font.weight.bold,
    textTransform: 'uppercase',
    color: 'rgba(247,244,237,0.62)',
  },
  tallyMiss: {
    margin: 0,
    lineHeight: 1.25,
    fontWeight: font.weight.bold,
    color: 'rgba(247,244,237,0.62)',
  },
  tallyName: {
    margin: 0,
    textTransform: 'uppercase',
    lineHeight: 1.2,
    fontWeight: font.weight.heavy,
    letterSpacing: '-0.2px',
    color: color.primaryDark,
  },

  nowVoting: {
    flexShrink: 0,
    margin: '0 0 18px',
    textAlign: 'center',
    fontSize: 13,
    fontWeight: font.weight.semibold,
    color: 'rgba(244,241,234,0.58)',
  },
  nowVotingName: { color: CREAM, fontWeight: font.weight.heavy },

  closer: {
    flexShrink: 0,
    margin: '22px 0 0',
    textAlign: 'center',
    fontSize: 14.5,
    lineHeight: 1.35,
    fontWeight: font.weight.semibold,
    color: 'rgba(244,241,234,0.72)',
  },
  closerStrong: { color: CREAM, fontWeight: font.weight.heavy },

  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
};
